import { useUnit } from 'effector-react';
import { CircleSlash2 } from 'lucide-react';
import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { viewerModel } from 'entities/viewer';
import { AccountWithWalletsAndPermissions } from 'shared/api';
import {
	ConfirmModalProps,
	createConfirmModal,
	ModalFormActionIconProps,
	ModalFormButtonProps,
} from 'shared/ui';
import { UnblockAccountModel } from '../../model';

export const createUnblockAccountModal = (model: UnblockAccountModel) => {
	const ConfirmModal = createConfirmModal(model);

	const UnblockAccountModal = forwardRef<HTMLDivElement, ConfirmModalProps>(({ ...props }, ref) => {
		const { t } = useTranslation();

		const [account] = useUnit([model.$modalData]);

		return (
			<ConfirmModal
				ref={ref}
				title={t('unblock-account.title')}
				cancelButtonProps={{
					children: t('unblock-account.cancel'),
				}}
				submitButtonProps={{
					variant: 'outline',
					children: t('unblock-account.unblock'),
				}}
				{...props}
			>
				{t('unblock-account.description', { name: account?.name })}
			</ConfirmModal>
		);
	});

	const UnblockAccountModalButton = forwardRef<
		HTMLButtonElement,
		ModalFormButtonProps<AccountWithWalletsAndPermissions>
	>(({ modalData, ...props }, ref) => {
		const { t } = useTranslation();

		const [viewerAccountId, canCreateSubAccounts] = useUnit([
			viewerModel.$accountId,
			viewerModel.$canCreateSubAccounts,
		]);

		return (
			<ConfirmModal.Button
				ref={ref}
				data-test-id='unblock-account-button'
				variant='outline'
				disabled={viewerAccountId === modalData.id || !canCreateSubAccounts}
				leftSection={<CircleSlash2 />}
				modalData={modalData}
				{...props}
			>
				{t('unblock-account.unblock')}
			</ConfirmModal.Button>
		);
	});

	const UnblockAccountModalActionIcon = forwardRef<
		HTMLButtonElement,
		ModalFormActionIconProps<AccountWithWalletsAndPermissions>
	>(({ modalData, ...props }, ref) => {
		const [viewerAccountId, canCreateSubAccounts] = useUnit([
			viewerModel.$accountId,
			viewerModel.$canCreateSubAccounts,
		]);

		return (
			<ConfirmModal.ActionIcon
				ref={ref}
				data-test-id='unblock-account-action-icon'
				variant='ghost-destructive'
				disabled={viewerAccountId === modalData.id || !canCreateSubAccounts}
				modalData={modalData}
				{...props}
			>
				<CircleSlash2 />
			</ConfirmModal.ActionIcon>
		);
	});

	return Object.assign(UnblockAccountModal, {
		Button: UnblockAccountModalButton,
		ActionIcon: UnblockAccountModalActionIcon,
	});
};
